import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface Palette {
    id: string;
    name: string;
    primary: string;
    primaryContent: string;
    secondary: string;
    secondaryLight: string;
    backgroundLight: string;
    backgroundDark: string;
    surfaceLight: string;
    surfaceDark: string;
    textMain: string;
    textMuted: string;
}

export const PALETTES: Palette[] = [
    {
        id: 'classic-pos',
        name: 'Classic POS',
        primary: '#f2f20d',
        primaryContent: '#181811',
        secondary: '#393e7f',
        secondaryLight: '#89bcf8',
        backgroundLight: '#f8f8f5',
        backgroundDark: '#172e50',
        surfaceLight: '#ffffff',
        surfaceDark: '#0f1f3a',
        textMain: '#181811',
        textMuted: '#8a8a60',
    },
    {
        id: 'spice-route',
        name: 'Spice Route',
        primary: '#0f1f3a',
        primaryContent: '#ffffff',
        secondary: '#89bcf8',
        secondaryLight: '#bfdbfe',
        backgroundLight: '#f8fafc',
        backgroundDark: '#020617',
        surfaceLight: '#ffffff',
        surfaceDark: '#0f172a',
        textMain: '#0f172a',
        textMuted: '#64748b',
    },
    {
        id: 'emerald',
        name: 'Emerald',
        primary: '#10b981',
        primaryContent: '#ffffff',
        secondary: '#064e3b',
        secondaryLight: '#6ee7b7',
        backgroundLight: '#f0fdf4',
        backgroundDark: '#022c22',
        surfaceLight: '#ffffff',
        surfaceDark: '#064e3b',
        textMain: '#064e3b',
        textMuted: '#059669',
    }
];

interface ThemeContextType {
    palette: Palette;
    setPalette: (paletteId: string) => void;
    isDark: boolean;
    setIsDark: (isDark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [paletteId, setPaletteId] = useState(() => localStorage.getItem('pos_palette_id') || 'classic-pos');
    const [isDark, setIsDarkState] = useState(() => localStorage.getItem('pos_theme_dark') === 'true');

    const palette = PALETTES.find(p => p.id === paletteId) || PALETTES[0];

    useEffect(() => {
        localStorage.setItem('pos_palette_id', paletteId);
        localStorage.setItem('pos_theme_dark', String(isDark));

        const root = window.document.documentElement;

        // Apply Palette Colors
        root.style.setProperty('--primary', palette.primary);
        root.style.setProperty('--primary-content', palette.primaryContent);
        root.style.setProperty('--secondary', palette.secondary);
        root.style.setProperty('--secondary-light', palette.secondaryLight);
        root.style.setProperty('--background-light', palette.backgroundLight);
        root.style.setProperty('--background-dark', palette.backgroundDark);
        root.style.setProperty('--surface-light', palette.surfaceLight);
        root.style.setProperty('--surface-dark', palette.surfaceDark);
        root.style.setProperty('--text-main', palette.textMain);
        root.style.setProperty('--text-muted', palette.textMuted);

        // Apply Mode
        if (isDark) {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, [palette, isDark, paletteId]);

    const setPalette = (id: string) => {
        setPaletteId(id);
    };

    const setIsDark = (dark: boolean) => {
        setIsDarkState(dark);
    };

    return (
        <ThemeContext.Provider value={{ palette, setPalette, isDark, setIsDark }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
